/**
 * useFamilyChatNotifications Hook
 * Monitors family chat for new messages and sends notifications
 */

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotifications } from './useNotifications';
import { apiGet } from '@/services/api-client';
import { hasStoredExpoPushToken } from '@/utils/expo-push-token';

interface FamilyChatMessage {
  id: string;
  fromName?: string;
  fromId?: string;
  text: string;
  createdAt: string;
}

export function useFamilyChatNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { user, isAuthenticated } = useAuthStore();
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Only run if user is authenticated, has a family, and notifications are enabled
    if (!isAuthenticated || !user?.familyId || !notificationSettings.familyChat || !permissionStatus.granted) {
      return;
    }

    // Skip if user has push token (mobile app) - they get backend push notifications instead
    const hasPushToken = hasStoredExpoPushToken();
    if (hasPushToken) {
      console.log('[useFamilyChatNotifications] Skipping - user has push token, will get backend notifications');
      return;
    }

    // Poll for new messages every 10 seconds
    const intervalId = setInterval(async () => {
      // Check for push token before each poll - stop polling if token exists
      const hasPushToken = hasStoredExpoPushToken();
      if (hasPushToken) {
        console.log('[useFamilyChatNotifications] Push token detected, stopping polling');
        clearInterval(intervalId);
        return;
      }

      try {
        const response = await apiGet<{ messages: FamilyChatMessage[] }>('/family/messages');
        if (!response.success || !response.data?.messages) return;

        const messages = response.data.messages;

        // On first run, just mark all messages as seen (don't notify for existing messages)
        if (!isInitializedRef.current) {
          messages.forEach(msg => seenMessageIdsRef.current.add(msg.id));
          isInitializedRef.current = true;
          return;
        }

        // Check for new messages from other users
        for (const message of messages) {
          // Skip if already seen
          if (seenMessageIdsRef.current.has(message.id)) {
            continue;
          }
          
          // Skip if message is from current user
          if (message.fromId === user.id) {
            seenMessageIdsRef.current.add(message.id);
            continue;
          }

          // Mark as seen
          seenMessageIdsRef.current.add(message.id);

          // Send notification for new message
          const senderName = message.fromName || 'Familjemedlem';
          const messagePreview = message.text.length > 100 
            ? message.text.substring(0, 100) + '...' 
            : message.text;

          await sendNotification({
            title: 'Familjechatten',
            body: `${senderName}: ${messagePreview}`,
            data: {
              type: 'family-chat',
              messageId: message.id,
              url: '/app/family',
            },
          });
        }

        // Clean up old message IDs from memory (keep last 100)
        if (seenMessageIdsRef.current.size > 100) {
          const messageIds = Array.from(seenMessageIdsRef.current);
          seenMessageIdsRef.current = new Set(messageIds.slice(-100));
        }
      } catch (error) {
        console.error('[useFamilyChatNotifications] Failed to check messages:', error);
      }
    }, 10 * 1000); // 10 seconds

    // Initial load - mark existing messages as seen
    apiGet<{ messages: FamilyChatMessage[] }>('/family/messages')
      .then(response => {
        if (response.success && response.data?.messages) {
          response.data.messages.forEach(msg => seenMessageIdsRef.current.add(msg.id));
          isInitializedRef.current = true;
        }
      })
      .catch(error => {
        console.error('[useFamilyChatNotifications] Failed to initialize:', error);
      });

    return () => {
      clearInterval(intervalId);
      isInitializedRef.current = false;
    };
  }, [isAuthenticated, user?.familyId, user?.id, notificationSettings.familyChat, permissionStatus.granted, sendNotification]);
}
